import { useState, KeyboardEvent, SyntheticEvent } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "~/utils";

interface DataSectionProps {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  isOpen?: boolean;
  onToggle?: () => void;
  testId?: string;
  level?: 0 | 1;
  actions?: React.ReactNode;
}

export const DataSection = ({
  title,
  children,
  defaultOpen = false,
  isOpen: controlledOpen,
  onToggle,
  testId,
  level = 0,
  actions,
}: DataSectionProps) => {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;

  const handleToggle = () => {
    if (onToggle) onToggle();
    if (!isControlled) setInternalOpen((prev) => !prev);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleToggle();
    }
  };

  const stopPropagation = (event: SyntheticEvent) => {
    event.stopPropagation();
  };

  return (
    <div className="flex flex-col" data-testid={testId}>
      <div
        role="button"
        tabIndex={0}
        aria-label={`${title} section`}
        aria-expanded={isOpen}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        className={cn(
          "flex w-full cursor-pointer items-center justify-between px-4 py-2 min-h-10 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          level === 0 && "bg-secondary-50",
          level === 1 && "bg-secondary-50/50",
        )}
      >
        <p
          className={cn(
            "font-semibold text-secondary-500",
            level === 0 && "text-xs",
            level === 1 && "text-xs font-medium",
          )}
        >
          {title}
        </p>
        {/** biome-ignore lint/a11y/noStaticElementInteractions: presentational wrapper that stops clicks on the actions from toggling the section */}
        <div
          className="flex items-center gap-3"
          onClick={stopPropagation}
          onKeyDown={stopPropagation}
          role="presentation"
        >
          {isOpen && actions}
          <ChevronDown
            aria-hidden="true"
            className={cn(
              "h-4 w-4 transform text-secondary-500 transition-transform duration-300",
              !isOpen && "rotate-180",
              level === 1 && "h-3.5 w-3.5",
            )}
          />
        </div>
      </div>
      {isOpen && (
        <div className={cn("px-4 py-2", level === 1 && "pl-6")}>{children}</div>
      )}
    </div>
  );
};
